import React, { useCallback, useEffect, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View
} from "react-native";

import { format, parseISO } from "date-fns";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";

import { ChantingService } from "../../../services/ChantingService";
import { ChantingDto } from "../../../models/ChantingModels";
import { useAuth } from "../../../../context/AuthContext";
import { useFocusEffect, useNavigation } from "@react-navigation/native";

export function ChantingScreen() {

    const { user } = useAuth();
    const navigation = useNavigation<any>();

    const today = format(new Date(), "yyyy-MM-dd");

    const [date, setDate] = useState(today);

    const [chantingId, setChantingId] = useState<string | null>(null);

    const [before8am, setBefore8am] = useState("0");
    const [before12pm, setBefore12pm] = useState("0");
    const [before6pm, setBefore6pm] = useState("0");
    const [after6pm, setAfter6pm] = useState("0");
    const [isEditable, setIsEditable] = useState(true);

    const [monthlyMala, setMonthlyMala] = useState(0);
    const [monthlyMarks, setMonthlyMarks] = useState(0);

    const [loading, setLoading] = useState(false);

    const totalMala =
        (parseInt(before8am) || 0) +
        (parseInt(before12pm) || 0) +
        (parseInt(before6pm) || 0) +
        (parseInt(after6pm) || 0);

    const totalMarks =
        ((parseInt(before8am) || 0) * 3) +
        ((parseInt(before12pm) || 0) * 2) +
        ((parseInt(before6pm) || 0) * 1) +
        ((parseInt(after6pm) || 0) * 0);

    useEffect(() => {
        loadData();
    }, [date]);

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    setDate(format(selectedDate, "yyyy-MM-dd"));
                }
            }
        });

    };

    useFocusEffect(
        useCallback(() => {
            loadData();
        }, [date])
    );

    const loadData = async () => {

        try {

            setLoading(true);

            const res = await ChantingService.getAgainstDate(date);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load chanting");
                return;
            }
debugger
            const d = res.data;

            setChantingId(d.chantingId);

            setMonthlyMala(d.monthlyMala);
            setMonthlyMarks(d.monthlyMarks);

            setBefore8am(String(d.before8am ?? 0));
            setBefore12pm(String(d.before12pm ?? 0));
            setBefore6pm(String(d.before6pm ?? 0));
            setAfter6pm(String(d.after6pm ?? 0));
            setIsEditable(d.isEditable);

        }
        catch (e: any) {
            Flash.error(e?.message || "Load failed");
        }
        finally {
            setLoading(false);
        }

    };

    const save = async () => {

        if (!user?.userId) {
            Flash.error("User not found");
            return;
        }

        if (totalMala === 0) {
            Flash.error("Please enter rounds chanted");
            return;
        }

        const payload: ChantingDto = {
            chantingId,
            userId: user.userId,
            date,
            before8am: parseInt(before8am) || 0,
            before12pm: parseInt(before12pm) || 0,
            before6pm: parseInt(before6pm) || 0,
            after6pm: parseInt(after6pm) || 0,
            totalMala,
            totalMarks
        };

        try {
            setLoading(true);

            const res = chantingId
                ? await ChantingService.update(payload)
                : await ChantingService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Save failed");
                return;
            }

            Flash.success("Success!", "Chanting Saved Successfully!");
            navigation.navigate("ChantingHistory");
        }
        catch (e: any) {
            Flash.error(e?.message || "Save failed");
        }
        finally {
            setLoading(false);
        }

    };

    return (

        <DivineBackground overlayOpacity={0.92}>

            <Header title="Chanting Seva" showBack />

            <ScrollView contentContainerStyle={styles.page} showsVerticalScrollIndicator={false}>

                {/* Summary */}

                <View style={styles.summaryRow}>

                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryLabel}>Month Mala</Text>
                        <Text style={styles.summaryValue}>{monthlyMala}</Text>
                    </View>

                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryLabel}>Month Marks</Text>
                        <Text style={styles.summaryValue}>{monthlyMarks}</Text>
                    </View>

                </View>

                {/* Entry Card */}

                <View style={styles.card}>

                    <View style={styles.topRow}>
                        <Pressable onPress={openDatePicker} style={styles.dateChip} disabled={loading}>
                            <Text style={styles.dateChipIcon}>📅</Text>
                            <Text style={styles.dateChipText}>
                                {format(new Date(date), "dd-MM-yyyy")}
                            </Text>
                        </Pressable>

                        <Pressable onPress={() => navigation.navigate("ChantingHistory" as never)} style={styles.historyBtn}>
                            <Text style={styles.historyIcon}>🕘</Text>
                            <Text style={styles.historyText}>History</Text>
                        </Pressable>

                    </View>

                    <Input
                        label="Before 8:00 AM (x3 Marks)"
                        keyboardType="number-pad"
                        value={before8am}
                        onChangeText={(t) => setBefore8am(t.replace(/[^\d-]/g, ""))}
                    />

                    <Input
                        label="Before 12:00 PM (x2 Marks)"
                        keyboardType="number-pad"
                        value={before12pm}
                        onChangeText={(t) => setBefore12pm(t.replace(/[^\d-]/g, ""))}
                    />

                    <Input
                        label="Before 6:00 PM (x1 Mark)"
                        keyboardType="number-pad"
                        value={before6pm}
                        onChangeText={(t) => setBefore6pm(t.replace(/[^\d-]/g, ""))}
                    />

                    <Input
                        label="After 6:00 PM (0 Mark)"
                        keyboardType="number-pad"
                        value={after6pm}
                        onChangeText={(t) => setAfter6pm(t.replace(/[^\d-]/g, ""))}
                    />

                    {/* Totals */}

                    <View style={styles.totalRow}>

                        <View style={styles.totalBox}>
                            <Text style={styles.totalLabel}>Total Mala</Text>
                            <Text style={styles.totalValue}>{totalMala}</Text>
                        </View>

                        <View style={styles.totalBox}>
                            <Text style={styles.totalLabel}>Total Marks</Text>
                            <Text style={styles.totalValue}>{totalMarks}</Text>
                        </View>

                    </View>

                    {isEditable && (
                        <Button
                            title={loading ? "Saving..." : "Save"}
                            onPress={save}
                            disabled={loading}
                        />
                    )}

                </View>

            </ScrollView>

            {loading && (
                <View style={styles.loading}>
                    <ActivityIndicator size="large" />
                </View>
            )}

        </DivineBackground>

    );

}

const styles = StyleSheet.create({

    page: {
        padding: 16,
        gap: 14,
        paddingBottom: 40
    },

    summaryRow: {
        flexDirection: "row",
        gap: 10
    },

    summaryCard: {
        flex: 1,
        backgroundColor: "rgba(255,255,255,0.9)",
        borderRadius: 14,
        padding: 12,
        alignItems: "center",
        borderWidth: 1,
        borderColor: "rgba(249,115,22,0.25)"
    },

    summaryLabel: {
        fontSize: 11,
        color: "#6B7280",
        fontWeight: "700"
    },

    summaryValue: {
        fontSize: 20,
        fontWeight: "900",
        color: "#EA580C"
    },

    card: {
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: 16,
        padding: 16,
        gap: 10,
        borderWidth: 1,
        borderColor: "rgba(249,115,22,0.25)"
    },

    topRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6
    },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(249,115,22,0.15)",
        borderWidth: 1,
        borderColor: "rgba(236,72,153,0.22)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },
    dateChipIcon: { fontSize: 14, opacity: 0.9 },
    dateChipText: { fontSize: 13, fontWeight: "900", color: "rgba(17,24,39,0.85)" },

    dateBox: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 10,
        backgroundColor: "#F3F4F6"
    },

    calendarIcon: { marginRight: 6 },

    dateText: {
        fontWeight: "800",
        fontSize: 13
    },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 14,
        backgroundColor: "rgba(255,237,213,0.35)",
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
    },

    historyIcon: { fontSize: 14 },
    historyText: { fontSize: 12, fontWeight: "900", color: "#9A3412" },

    totalRow: {
        flexDirection: "row",
        gap: 10,
        marginTop: 6
    },

    totalBox: {
        flex: 1,
        alignItems: "center",
        padding: 10,
        backgroundColor: "#F9FAFB",
        borderRadius: 10
    },

    totalLabel: {
        fontSize: 11,
        color: "#6B7280"
    },

    totalValue: {
        fontSize: 18,
        fontWeight: "900",
        color: "#111827"
    },

    loading: {
        position: "absolute",
        top: "50%",
        alignSelf: "center"
    }

});